import { NavigationOutlined } from '@mui/icons-material';
import { Divider, Grid, Stack, Theme, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { Button, DateTimeRangeSelector, Modal, ToggleSelect } from '@perry-weather/component-library';
import { useMemo } from 'react';
import { useUsersQuery } from '../../Services/API';
import { ConfirmationDialog } from '../Base/ConfirmationDialog';
import { useAuth } from 'oidc-react';
import mixpanel from 'mixpanel-browser';
import { TrackableEvents } from '../Tracked/events';
import { withClickTracking } from '../Tracked/withMixpanelTracking';
import { ReactComponent as WeatherConditionsIcon } from '../../Assets/HistoricalReportIcons/weather_conditions.svg';
import { ReactComponent as PolicyDelaysIcon } from '../../Assets/HistoricalReportIcons/policy_delays.svg';
import PermissionGate, { usePermissionGate } from '../Shared/PermissionGate';
import { Permissions } from '../../Constants/Constants';
import WeatherObservations from './Report/WeatherObservations';
import Policies from './Report/Policies';
import ExportSettings from './Report/ExportSettings';
import { useHistoricalReportForm } from '../../hooks/useHistoricalReportForm';
import { MAX_DAYS_BY_GRANULARITY, POLICIES, ReportType } from '../../Constants/HistoricalReportConstants';

interface ModalProps {
  onSubmit?: React.MouseEventHandler;
  onCancel?: React.MouseEventHandler;
  open: boolean;
  handleClose: Function;
  hardwareId: number;
  weatherStationId?: string;
  organizationLocationId?: string | null;
}

const VIEWS = [
  {
    name: ReportType.WeatherConditions,
    icon: <WeatherConditionsIcon width={'26px'} height={'26px'} strokeWidth={10} />,
  },
  {
    name: ReportType.PolicyDelays,
    icon: <PolicyDelaysIcon width={'20px'} height={'20px'} strokeWidth={10} />,
  },
];

const TRACK_PROPERTIES = {
  button_location: 'Historical Report',
  button_feature: 'Historical Export',
  button_type: 'button',
};

const TrackedButton = withClickTracking(Button)(TrackableEvents.ButtonClicked, TRACK_PROPERTIES);

const useStyles = (theme: Theme) => ({
  gridSection: {
    background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.action.disabled,
    padding: '22px',
    borderRadius: '8px',
    height: '100%',
  },
  toggleViewGroup: {
    gap: 1,
    '.MuiToggleButtonGroup-grouped:not(:last-of-type)': {
      borderRadius: 0,
    },
    '.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
      borderRadius: 0,
    },
  },
  toggleView: {
    gap: '6px',
    border: 0,
    padding: '10px',
    fontWeight: 'bold',
    textTransform: 'none',
    color: theme.palette.text.secondary,
    height: 'fit-content',
    marginBottom: '16px',
    ':hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
      borderBottom: `3px solid ${theme.palette.text.primary}`,
      svg: {
        path: {
          color: theme.palette.text.primary,
        },
      },
    },
  },
  modalDivider: { width: 'calc(100% + 64px)', margin: '20px 0', transform: 'translate(-32px, 0)' },
  pill: {
    padding: '6px 12px',
    borderRadius: '6px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    width: 'fit-content',
    height: 'fit-content',
    backgroundColor: theme.palette.background.disabled,
    ':hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.background.default,
    },
  },
  select: {
    width: 'fit-content',
    height: 'fit-content',
    '& .MuiOutlinedInput-input': {
      padding: '6px 32px 6px 14px',
      zIndex: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1,
      borderColor: theme.palette.border.input,
      borderRadius: 2,
    },
  },
});

export function HistoricalReportModal(props: ModalProps) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { open, handleClose, hardwareId, weatherStationId, organizationLocationId } = props;

  const { userData: userAuthData } = useAuth();
  const { data: users } = useUsersQuery();

  const hasMesonetAccess = usePermissionGate({
    allowedPermissions: [Permissions.MesonetAccess],
  });

  const form = useHistoricalReportForm({
    open,
    users,
    hardwareId,
    userAuthData,
    organizationLocationId,
    weatherStationId,
    hasMesonetAccess,
  });

  const handleModalCancel = () => {
    if (form.hasUnsavedChanges) return;
    props.handleClose(false);
  };

  const handleModalClose = async () => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      ...TRACK_PROPERTIES,
      button_label: 'Confirm Report Cancellation',
    });

    props.handleClose(false);
  };

  const confirmDialogButton = useMemo(() => {
    return (
      <TrackedButton
        mpExtraData={{ button_label: 'Generate Report' }}
        disabled={!form.isExportEnabled}
        type='primary'
        sx={{
          base: {
            fontWeight: 'bold',
            gap: '6px',
          },
        }}>
        <NavigationOutlined sx={{ transform: 'rotate(0.25turn)' }} />
        Generate Report
      </TrackedButton>
    );
  }, [form.isExportEnabled]);

  return (
    <Modal
      sx={{
        paper: {
          flexGrow: '1',
          marginLeft: { md: '15%', sm: '10%', xs: '5%' },
          marginRight: { md: '15%', sm: '10%', xs: '5%' },
          overFlowX: 'hidden',
          backgroundColor: theme.palette.background.default,
        },
      }}
      modalState={{
        open: open,
        handleOpen: () => {},
        handleClose: () => {
          handleModalCancel();
        },
      }}>
      <Grid container>
        <Stack>
          <Typography variant='h5' fontWeight='bold'>
            Export Data
          </Typography>
          <Typography color='textSecondary' overflow='visible' noWrap>
            Customize and export the data you need.
          </Typography>
        </Stack>

        <Divider sx={classes.modalDivider} />

        <Stack direction='row' width='100%' justifyContent='space-between' gap={2} alignItems='center'>
          {userAuthData && (
            <PermissionGate
              allowedPermissions={[Permissions.HistoricalReportPolicyDelaysAccess]}
              fallback={<div style={{ height: '60px' }} />}>
              {VIEWS.length > 1 ? (
                <ToggleButtonGroup
                  exclusive
                  color='primary'
                  value={form.selectedView}
                  onChange={(_, value) => form.handleViewChange(value)}
                  aria-label='View'
                  sx={classes.toggleViewGroup}>
                  {VIEWS.map((view, index) => (
                    <ToggleButton key={index} value={view.name} sx={classes.toggleView}>
                      {view.icon}
                      {view.name}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : (
                <div style={{ height: '60px' }} />
              )}
            </PermissionGate>
          )}

          {form.selectedView === ReportType.WeatherConditions && (
            <ToggleSelect
              sx={{
                container: { justifySelf: 'flex-end', alignSelf: 'self-start' },
                menu: { right: '0', padding: '14px', paddingRight: 4, textWrap: 'nowrap' },
                button: {
                  '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: theme.palette.background.disabled,
                    color: theme.palette.common.white,
                  },
                },
              }}
              menuWidth={300}
              maxItemsOnView={5}
              showCount
              value=''
              title='Weather Station'
              showSelections={false}
              showSelectAll={form.weatherStations.length > 1}
              showSearchbox={form.weatherStations.length > 1}
              options={form.weatherStations}
              selections={form.settings.weatherStations.map(x => x.toString())}
              onChange={(_, stations) => form.handleUpdateWeatherStations(stations)}
            />
          )}

          {form.selectedView === ReportType.PolicyDelays && (
            <ToggleSelect
              sx={{
                container: { justifySelf: 'flex-end', alignSelf: 'self-start' },
                menu: { right: '0', padding: '14px', paddingRight: 4, textWrap: 'nowrap' },
                button: {
                  '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: theme.palette.background.disabled,
                    color: theme.palette.common.white,
                  },
                },
              }}
              menuWidth={300}
              maxItemsOnView={5}
              showCount
              value=''
              title='Locations'
              showSelections={false}
              showSelectAll={form.locations.length > 1}
              showSearchbox={form.locations.length > 1}
              options={form.locations}
              selections={form.settings.locations}
              onChange={(_, locations) => form.handleUpdateLocations(locations)}
            />
          )}
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack width='100%' sx={classes.gridSection}>
              <Typography variant='h6' fontWeight='bold'>
                Date Range
              </Typography>
              <Typography color='textSecondary' overflow='visible' noWrap>
                Select the date range for your report.
              </Typography>
              {!form.validations.isDateRangeValid && (
                <Typography color={theme.palette.error.light} overflow='visible'>
                  Your selected date range exceeds the {MAX_DAYS_BY_GRANULARITY[form.settings.granularity]}
                  -day limit for the {form.settings.observationsDataInterval} interval. Please adjust your date range to
                  continue.
                </Typography>
              )}
              <DateTimeRangeSelector
                disableFuture
                sx={{
                  container: { width: 'fit-content' },
                  button: {
                    marginTop: 1,
                    width: 'fit-content',
                    borderColor: form.validations.isDateRangeValid
                      ? theme.palette.border.main
                      : theme.palette.error.main,
                  },
                }}
                onChange={form.handleDateRangeChange}
              />
            </Stack>
          </Grid>

          {form.selectedView === ReportType.WeatherConditions && (
            <Grid item md={12} sm={12}>
              <Stack flexGrow={1} sx={classes.gridSection}>
                <WeatherObservations
                  isAllSelected={form.settings.weatherObservations.size >= form.allowedObservations.length}
                  selectedDataInterval={form.settings.observationsDataInterval}
                  isPrecipValid={form.validations.isPrecipValid}
                  showPrecipError={form.showPrecipError}
                  isDateRangeValid={form.validations.isDateRangeValid}
                  selectedGranularity={form.settings.granularity}
                  styles={{ chip: classes.pill }}
                  observationSwitchAllTrigger={form.observationSwitchAllTrigger}
                  handleSelectAllObservations={form.handleSelectAllObservations}
                  handleSelectedObservations={form.handleSelectedObservations}
                  handleObservationDataIntervalChange={form.handleObservationDataIntervalChange}
                />
              </Stack>
            </Grid>
          )}

          {form.selectedView === ReportType.PolicyDelays && (
            <Grid item md={12} sm={12}>
              <Stack flexGrow={1} sx={classes.gridSection}>
                <Policies
                  isAllSelected={form.settings.policies.size >= POLICIES.length}
                  selections={form.settings.policies}
                  policiesDataInterval={form.settings.policiesDataInterval}
                  isDateRangeValid={form.validations.isDateRangeValid}
                  styles={{ chip: classes.pill }}
                  policySwitchAllTrigger={form.policySwitchAllTrigger}
                  handleSelectAllPolicies={form.handleSelectAllPolicies}
                  handleSelectedPolicies={form.handleSelectedPolicies}
                  handlePolicyDataIntervalChange={form.handlePolicyDataIntervalChange}
                />
              </Stack>
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack width='100%' gap={2} sx={classes.gridSection}>
              <ExportSettings
                includeRecipients={form.settings.includeRecipients}
                recipients={form.groupEmails}
                settings={form.settings}
                currentUserEmail={userAuthData?.profile.email}
                maxChips={form.maxChips}
                recipientEmails={form.userIdEmail}
                fileName={form.fileName}
                styles={{ chip: classes.pill, select: classes.select }}
                updateSettings={form.updateSettings}
                updateMaxChips={form.updateMaxChips}
                handleRecipientsChange={form.handleRecipientsChange}
                handleChangeFileName={form.handleChangeFileName}
                handleSelectedFormats={form.handleSelectedFormats}
              />
            </Stack>
          </Grid>

          <Stack direction='row' justifyContent='end' width='100%' marginTop={3} gap={2}>
            {form.hasUnsavedChanges ? (
              <ConfirmationDialog
                confirmationQuestion='Discard Changes?'
                confirmationInfo='You have unsaved changes. Are you sure you want to exit? All changes will be lost.'
                completionTitle=''
                completionInfo=''
                button={<Button type='clear'>Cancel</Button>}
                onConfirm={handleModalClose}
                rejectButtonText='Keep Editing'
                onCancel={() =>
                  mixpanel.track(TrackableEvents.ButtonClicked, {
                    ...TRACK_PROPERTIES,
                    button_label: 'Keep Editing',
                  })
                }
              />
            ) : (
              <TrackedButton
                mpExtraData={{ button_label: 'Cancel' }}
                type='clear'
                onClick={() => props.handleClose(false)}>
                Cancel
              </TrackedButton>
            )}

            <ConfirmationDialog
              confirmationQuestion='Generate Report?'
              confirmationInfo='The report will be generated and sent via email.'
              completionTitle='The report is being generated.'
              completionInfo="You will receive an email to download the report once it's ready."
              disabled={!form.isExportEnabled}
              button={confirmDialogButton}
              onConfirm={form.handleGenerateReport}
              onCompletion={() => handleModalClose()}
            />
          </Stack>
        </Grid>
      </Grid>
    </Modal>
  );
}
